import { createFileRoute, useParams } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVerticalIcon, XIcon } from "lucide-react";

import { trpc } from "@/hooks/trpc";
import type {
  FermentationProcedureId,
  FermentationProcedureProtocolId,
  FermentationProtocolId,
} from "@brewbase/db/types";
import { FermentationProtocolModeEnum } from "@brewbase/db/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NumericInput } from "@/components/ui/numeric-input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ProcedureSequenceEntry } from "@/features/fermentation-procedure-editor/types";
import {
  cumulativeOffsetsH,
  formatSequenceDue,
} from "@/features/fermentation-procedure-editor/sequence-timing";

export const Route = createFileRoute(
  "/fermentations/procedures/$procedure_id/protocols",
)({
  component: ProtocolsPage,
});

const MODE_LABELS: Record<FermentationProtocolModeEnum, string> = {
  [FermentationProtocolModeEnum.BATCH]: "Batch",
  [FermentationProtocolModeEnum.FED_BATCH]: "Fed-batch",
  [FermentationProtocolModeEnum.CONTINUOUS]: "Continuous",
};

/** What the picker needs to know about a global protocol. */
interface ProtocolChoice {
  id: FermentationProtocolId;
  name: string;
  version: string;
  mode: FermentationProtocolModeEnum;
  isLocked: boolean;
}

type EntryUpdates = {
  protocolId?: FermentationProtocolId;
  offsetH?: string;
  defaultTransitionH?: string | null;
  notes?: string | null;
};

function ProtocolsPage() {
  const { procedure_id } = useParams({ from: Route.id });
  const procedureId = procedure_id as unknown as FermentationProcedureId;
  const utils = trpc.useUtils();

  const [selectedId, setSelectedId] =
    useState<FermentationProcedureProtocolId | null>(null);

  const { data: procedure, isLoading } =
    trpc.fermentationProcedures.getWithDetails.useQuery(procedureId);
  // No input lists the global protocols, the only ones a sequence may name.
  const { data: protocols } = trpc.fermentations.protocols.list.useQuery();

  const invalidate = () =>
    utils.fermentationProcedures.getWithDetails.invalidate(procedureId);

  const addEntry = trpc.fermentationProcedures.addSequenceEntry.useMutation({
    onSuccess: (entry) => {
      toast.success(`${entry.protocolName ?? "Protocol"} added`);
      setSelectedId(entry.id);
      invalidate();
    },
    onError: (error) => toast.error(`Failed to add protocol: ${error.message}`),
  });

  const updateEntry =
    trpc.fermentationProcedures.updateSequenceEntry.useMutation({
      onSuccess: () => invalidate(),
      onError: (error) => {
        toast.error(`Failed to update entry: ${error.message}`);
        invalidate();
      },
    });

  const removeEntry =
    trpc.fermentationProcedures.removeSequenceEntry.useMutation({
      onSuccess: () => {
        toast.success("Protocol removed from sequence");
        setSelectedId(null);
        invalidate();
      },
      onError: (error) =>
        toast.error(`Failed to remove entry: ${error.message}`),
    });

  const reorderSequence =
    trpc.fermentationProcedures.reorderSequence.useMutation({
      onSuccess: () => invalidate(),
      onError: (error) => {
        toast.error(`Failed to reorder sequence: ${error.message}`);
        invalidate();
      },
    });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // The service orders by position; sort defensively so the list is stable
  // whatever the cache holds mid-reorder.
  const entries = useMemo(() => {
    if (!procedure?.sequence) return [];
    return [...procedure.sequence].sort((a, b) => {
      const byPosition = a.position - b.position;
      return byPosition !== 0 ? byPosition : (a.id as number) - (b.id as number);
    });
  }, [procedure?.sequence]);

  const cumulative = useMemo(() => cumulativeOffsetsH(entries), [entries]);

  const protocolChoices = useMemo<ProtocolChoice[]>(
    () =>
      (protocols ?? []).map((p) => ({
        id: p.id,
        name: p.name ?? "Unnamed protocol",
        version: p.version ?? "?",
        mode: p.mode,
        isLocked: p.is_locked,
      })),
    [protocols],
  );

  const selectedEntry = useMemo(
    () => entries.find((e) => e.id === selectedId) ?? entries[0] ?? null,
    [entries, selectedId],
  );
  const selectedIndex = selectedEntry
    ? entries.findIndex((e) => e.id === selectedEntry.id)
    : -1;

  const isLocked = (procedure?.is_locked ?? false) || !!procedure?.archived_at;

  const handleAdd = (protocolId: FermentationProtocolId) => {
    if (isLocked) return;
    // Appends at the end. A zero offset means "same time as previous"; the
    // inspector is where the author sets the real spacing.
    addEntry.mutate({ procedureId, protocolId, offsetH: "0" });
  };

  const handleUpdate = (updates: EntryUpdates) => {
    if (isLocked || !selectedEntry) return;
    updateEntry.mutate({
      procedureId,
      entryId: selectedEntry.id,
      ...updates,
    });
  };

  const handleRemove = (entryId: FermentationProcedureProtocolId) => {
    if (isLocked) return;
    removeEntry.mutate({ procedureId, entryId });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isLocked) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = entries.findIndex((e) => e.id.toString() === active.id);
    const newIndex = entries.findIndex((e) => e.id.toString() === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...entries];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Offsets travel with their entry; whichever lands first is anchored at
    // inoculation. Reflect the move immediately, the server writes the same
    // positions and the invalidate that follows confirms them.
    utils.fermentationProcedures.getWithDetails.setData(procedureId, (old) =>
      old
        ? {
            ...old,
            sequence: reordered.map((e, idx) => ({
              ...e,
              position: idx,
              offset_h: idx === 0 ? "0" : e.offset_h,
            })),
          }
        : old,
    );

    reorderSequence.mutate({
      procedureId,
      orderedIds: reordered.map((e) => e.id),
    });
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3 h-full">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!procedure) {
    return <div className="p-4 text-sm">Procedure not found</div>;
  }

  const addControl =
    !isLocked && protocolChoices.length > 0 ? (
      <Select
        value=""
        onValueChange={(value) =>
          handleAdd(Number(value) as unknown as FermentationProtocolId)
        }
        disabled={addEntry.isPending}
      >
        <SelectTrigger className="h-7 w-56 text-xs">
          <SelectValue placeholder="Add protocol…" />
        </SelectTrigger>
        <SelectContent>
          {protocolChoices.map((p) => (
            <SelectItem key={p.id} value={p.id.toString()}>
              <ProtocolChoiceLabel choice={p} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-4 py-2 border-b flex-shrink-0">
        <div className="text-sm font-medium">Protocol sequence</div>
        <div className="text-xs text-muted-foreground flex-1">
          Which protocols a fermentation on this procedure runs, in order, and
          when each is due.
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-muted-foreground max-w-sm">
            <p className="text-sm mb-1">No protocol sequence.</p>
            <p className="text-xs mb-3">
              Fermentations on this procedure start without a protocol; apply
              one by hand from the timeline.
            </p>
            <div className="flex justify-center">{addControl}</div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12">
          <div className="md:col-span-5 min-h-0 border-r">
            <ScrollArea className="h-full">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={entries.map((e) => e.id.toString())}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="grid grid-cols-[24px_20px_1fr_auto_28px] gap-1 px-2 py-1.5 text-xs font-medium text-muted-foreground border-b bg-muted/30 sticky top-0">
                    <div></div>
                    <div className="text-right">#</div>
                    <div>Protocol</div>
                    <div className="text-right">Due</div>
                    <div></div>
                  </div>
                  <div>
                    {entries.map((entry, index) => (
                      <SequenceTableRow
                        key={entry.id}
                        entry={entry}
                        cumulativeH={cumulative[index] ?? 0}
                        selected={selectedEntry?.id === entry.id}
                        onClick={() => setSelectedId(entry.id)}
                        onDelete={() => handleRemove(entry.id)}
                        readOnly={isLocked}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              {addControl && <div className="p-2">{addControl}</div>}
            </ScrollArea>
          </div>

          <div className="md:col-span-7 min-h-0">
            {selectedEntry ? (
              <SequenceInspector
                key={selectedEntry.id}
                entry={selectedEntry}
                cumulativeH={cumulative[selectedIndex] ?? 0}
                protocolChoices={protocolChoices}
                onUpdate={handleUpdate}
                isLocked={isLocked}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground bg-muted/20">
                Select an entry to edit.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ======================== Sequence Table Row ========================

function SequenceTableRow({
  entry,
  cumulativeH,
  selected,
  onClick,
  onDelete,
  readOnly,
}: {
  entry: ProcedureSequenceEntry;
  cumulativeH: number;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  readOnly: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.id.toString(), disabled: readOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : "auto",
  } as React.CSSProperties;

  const sameTimeAsPrevious =
    entry.position > 0 && Number(entry.offset_h) === 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "grid grid-cols-[24px_20px_1fr_auto_28px] gap-1 items-center px-2 py-1.5 text-sm hover:bg-muted/50 cursor-pointer border-b border-border/50 " +
        (selected ? "bg-primary/5 border-l-2 border-l-primary" : "")
      }
      onClick={onClick}
    >
      <button
        {...attributes}
        {...listeners}
        className={
          "text-muted-foreground flex-shrink-0 p-0.5 " +
          (readOnly ? "cursor-default" : "cursor-grab hover:text-foreground")
        }
        aria-label="Drag to reorder"
        disabled={readOnly}
      >
        <GripVerticalIcon className="h-4 w-4" />
      </button>

      <div className="text-right text-xs text-muted-foreground font-mono">
        {entry.position + 1}
      </div>

      <div className="min-w-0 flex items-center gap-1.5">
        <span className="truncate font-medium">
          {entry.protocolName ?? "Unknown protocol"}
        </span>
        {entry.protocolVersion && (
          <span className="text-xs text-muted-foreground flex-shrink-0">
            v{entry.protocolVersion}
          </span>
        )}
        <span className="text-xs bg-olive-3 text-olive-11 px-1 py-0.5 flex-shrink-0">
          {MODE_LABELS[entry.protocolMode]}
        </span>
        {!entry.protocolIsLocked && (
          <span className="text-xs bg-muted text-muted-foreground px-1 py-0.5 flex-shrink-0">
            draft
          </span>
        )}
      </div>

      <div className="text-right text-xs font-mono whitespace-nowrap">
        <span>{formatSequenceDue(entry.position, cumulativeH)}</span>
        {sameTimeAsPrevious && (
          <span className="block font-sans text-muted-foreground">
            same time as previous
          </span>
        )}
      </div>

      {readOnly ? (
        <div />
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="h-5 w-5 p-0 hover:text-destructive hover:bg-destructive/10"
        >
          <XIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

// ======================== Sequence Inspector ========================

function SequenceInspector({
  entry,
  cumulativeH,
  protocolChoices,
  onUpdate,
  isLocked,
}: {
  entry: ProcedureSequenceEntry;
  cumulativeH: number;
  protocolChoices: ProtocolChoice[];
  onUpdate: (updates: EntryUpdates) => void;
  isLocked: boolean;
}) {
  const isFirst = entry.position === 0;

  // An entry may name a protocol the global list no longer offers (archived
  // since); keep it selectable so the trigger has something to show.
  const choices = useMemo<ProtocolChoice[]>(() => {
    if (protocolChoices.some((p) => p.id === entry.protocol_id)) {
      return protocolChoices;
    }
    return [
      ...protocolChoices,
      {
        id: entry.protocol_id,
        name: entry.protocolName ?? "Unknown protocol",
        version: entry.protocolVersion ?? "?",
        mode: entry.protocolMode,
        isLocked: entry.protocolIsLocked,
      },
    ];
  }, [protocolChoices, entry]);

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Entry {entry.position + 1}
          </Label>
          <div className="text-sm font-medium mt-1">
            {entry.protocolName ?? "Unknown protocol"}
            {entry.protocolVersion && (
              <span className="text-muted-foreground"> v{entry.protocolVersion}</span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Due {formatSequenceDue(entry.position, cumulativeH)}
          </div>
        </div>

        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Protocol
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            Only global protocols can be sequenced. A draft may still change
            before a run reaches it.
          </p>
          <Select
            value={entry.protocol_id.toString()}
            onValueChange={(value) =>
              onUpdate({
                protocolId: Number(value) as unknown as FermentationProtocolId,
              })
            }
            disabled={isLocked}
          >
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {choices.map((p) => (
                <SelectItem key={p.id} value={p.id.toString()}>
                  <ProtocolChoiceLabel choice={p} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-t pt-4 space-y-4">
          <div>
            <Label className="text-xs uppercase text-muted-foreground font-medium">
              {isFirst ? "Due" : "After previous entry"}
            </Label>
            {isFirst ? (
              <div className="text-sm mt-1">At inoculation</div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  Hours after the previous entry's switch. Zero means the two
                  are due together.
                </p>
                <div className="max-w-40">
                  <HoursField
                    value={entry.offset_h}
                    onCommit={(value) => onUpdate({ offsetH: value ?? "0" })}
                    placeholder="0"
                    disabled={isLocked}
                  />
                </div>
              </>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase text-muted-foreground font-medium">
              Transition
            </Label>
            <p className="text-xs text-muted-foreground mb-2">
              Rampable entries reach their target over this; blank steps.
            </p>
            <div className="max-w-40">
              <HoursField
                value={entry.default_transition_h}
                onCommit={(value) => onUpdate({ defaultTransitionH: value })}
                placeholder="step"
                disabled={isLocked}
              />
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Notes
          </Label>
          <Textarea
            defaultValue={entry.notes ?? ""}
            onBlur={(e) => {
              const next = e.target.value;
              if (next !== (entry.notes ?? "")) {
                onUpdate({ notes: next || null });
              }
            }}
            placeholder="Anything an operator should know before this switch…"
            rows={3}
            className="mt-2 text-sm"
            readOnly={isLocked}
          />
        </div>
      </div>
    </ScrollArea>
  );
}

function ProtocolChoiceLabel({ choice }: { choice: ProtocolChoice }) {
  return (
    <span className="flex items-center gap-1.5">
      <span>{choice.name}</span>
      <span className="text-xs text-muted-foreground">v{choice.version}</span>
      <span className="text-xs text-muted-foreground">
        {MODE_LABELS[choice.mode]}
      </span>
      {!choice.isLocked && (
        <span className="text-xs text-muted-foreground">draft</span>
      )}
    </span>
  );
}

/**
 * Commits on blur rather than per keystroke — the field writes straight to the
 * server and a mutation per character is not worth it. NumericInput keeps the
 * in-progress text itself, so no mirrored state is needed here.
 */
function HoursField({
  value,
  onCommit,
  placeholder,
  disabled,
}: {
  value: string | null;
  onCommit: (value: string | null) => void;
  placeholder: string;
  disabled: boolean;
}) {
  const draft = useRef<string | null>(value);

  return (
    <NumericInput
      precise
      min={0}
      value={value}
      onValueChange={(next) => {
        draft.current = next;
      }}
      onBlur={() => {
        if (draft.current !== value) onCommit(draft.current);
      }}
      postfix="h"
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}
